/**
 * The recipient list — **the screen the skip reasons were written for.**
 *
 * A campaign's numbers are a claim; this is the evidence. It answers the two
 * questions a person actually has, and neither of them is "show me the
 * recipients":
 *
 *   - *who is actually going to receive this?* — the waiting rows, which in a
 *     guarded environment are a handful of allow-listed addresses out of
 *     hundreds. This is the only place those addresses can be listed: the send
 *     guard's own shape carries a count and never the allow-list itself, so the
 *     addresses that will be written to are only knowable once the recipient
 *     list exists, and then they are this workspace's own contacts rather than
 *     somebody's configuration;
 *   - *why did the other 1,277 get nothing?* — filtered to one reason, with that
 *     reason's whole sentence above the table. "Skipped" beside a real
 *     customer's name is how a person concludes the product is broken.
 *
 * The address comes from the recipient row's own `dedupe_key` rather than from a
 * live join to `contacts`, which is why it survives a dataset re-import: the
 * record of who was written to must outlive the rows it was built from. A
 * recipient with no address on this channel shows a dash, and their skip reason
 * says exactly that.
 *
 * ## Slice 17: the empty table was answering the wrong question
 *
 * There are **four** ways this table comes back with no rows and the screen knew
 * about two of them. A search that matched nothing said so; **everything else
 * said "nobody on this campaign matches that filter", including the case where
 * no filter was applied at all** — a campaign whose recipient list was built and
 * came out empty, because nobody in the segment could be reached on this
 * channel. That is not a filter result. It is the reason the campaign will send
 * nothing, and it is worth arriving at here rather than by subtracting two
 * numbers on the card above. That branch is written from `filter`, which this
 * component already receives, so nothing new is fetched to tell them apart.
 *
 * The fourth is **an empty page over a list that is not empty**, and the first
 * version of the fix reasoned about it in a comment and then guarded the wrong
 * branch. `filter` is owned by `CampaignDetailPage` and `page` is owned here,
 * so a quick-filter chip or a deep link narrowed the list and left the reader
 * on page 5 of it: the API answered with no rows, and the filtered branch —
 * which the ternary reaches before the guarded one — printed "Nobody on this
 * campaign matches that filter" over forty skipped people. That is the same
 * class of lie as the one above, one level in, on the screen whose entire
 * purpose is naming who got nothing and why.
 *
 * Both ends are held now, and they are not redundant. Narrowing resets `page`
 * to 1, so the state is rare; and every absolute sentence is gated on
 * `pagination.total === 0`, because the list is live — `refreshKey` moves while
 * a send runs — so a page can fall off the end without anybody touching a
 * filter. A guard that is only correct because of a `useEffect` elsewhere in
 * the file is a guard that stops being correct when somebody moves the effect.
 *
 * The prose was cut in the two places it was explaining rather than stating: the
 * not-materialised paragraph and `describeList`'s scheduled and sending lines.
 * What each one *had* to keep is the same sentence in both — that the waiting
 * rows are the people who will actually receive the message. In a guarded
 * environment that is a handful of addresses out of hundreds, and it is the only
 * screen in the product that can name them.
 *
 * ## Slice 25: the list is asked for, not assumed
 *
 * `wend-designer` measured this page at 1,280 × 800: 7.7 screens, and **this
 * component is 41% of the scroll and 55% of the DOM** — 2,509px and 341 nodes
 * for 25 rows, refetched on every poll tick while a send runs. Slice 19 already
 * moved it below the figures, so the ordering complaint is settled; what is left
 * is the cost, paid by every reader whether or not they came here for rows. The
 * owner put it plainly: *"I don't think I need that metrics in the first page of
 * campaign — that can sit somewhere, and should come up when I want to see it."*
 * Behind the control the page is 4.6 screens.
 *
 * **Nothing about the list is hidden by hiding it.** The card, the count and
 * `describeList`'s line stay; so does the not-materialised paragraph, which is a
 * fact about the campaign rather than a row and never had a control over it. The
 * count comes from `campaignProgressReading` — the campaign response — so "1,278
 * recipients" is on screen without a request for any of them.
 *
 * **Every route in still lands on rows.** `open` is owned by
 * `CampaignDetailPage`, because the four places that focus this list are up
 * there: scheduling, a skip reason from the progress breakdown, "Show who it
 * held back", and "which ones got nothing?". Each opens the list as it filters
 * it. A fifth written by somebody who has not read this file goes through the
 * same helper, which is why that helper exists rather than four `setOpen(true)`
 * calls.
 *
 * **This disclosure drops its children, and `MoreDetail`'s deliberately does
 * not.** That component's header argues — correctly — that a folded sentence
 * must stay in the DOM so Ctrl+F can find it, and that a conditionally rendered
 * detail is a detail nobody can search for. The argument does not transfer here,
 * and the reason is that this is not prose: it is page 1 of up to 52. Ctrl+F
 * over it searches 25 of 1,278 people and silently misses the other 1,253 — it
 * is *already* the wrong tool, and the search box inside queries every page on
 * the server. Keeping 341 nodes mounted would buy a worse search and pay for it
 * with the fetch this change exists to stop.
 */
import {
  CAMPAIGN_SKIP_REASONS,
  DEFAULT_PAGE_SIZE,
  campaignProgressReading,
  type Campaign,
  type CampaignRecipient,
  type CampaignSkipReason,
  type RecipientStatus,
} from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Input,
  ScopeChips,
  Spinner,
  type DataTableColumn,
} from '@wend/ui';
import { ListFilter, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { listCampaignRecipients } from '../../lib/api/campaigns';
import { errorMessage } from '../../lib/api/errors';
import {
  RECIPIENT_STATUS_DISPLAY,
  SKIP_REASON_TONE,
  messageStatusMeaning,
} from '../../lib/campaign-display';
import { formatDateTime, pluralise } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { RecipientBadgeLegend } from './RecipientBadgeLegend';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

export interface RecipientFilter {
  readonly status: RecipientStatus | null;
  readonly skipReason: CampaignSkipReason | null;
}

export const NO_RECIPIENT_FILTER: RecipientFilter = { status: null, skipReason: null };

export interface CampaignRecipientsProps {
  campaign: Campaign;
  filter: RecipientFilter;
  onFilterChange: (filter: RecipientFilter) => void;
  /**
   * Changes whenever the campaign's own counts move, so an open list follows a
   * send in progress rather than showing the moment it was first drawn.
   */
  refreshKey: string;
  /**
   * Whether the rows are shown. Owned by `CampaignDetailPage` because the four
   * routes *into* this list are up there — see the gate section in the header.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The card, the count, and the control that asks for the rows.
 *
 * Everything that fetches is in {@link RecipientList} below, mounted only while
 * `open`. That split is the whole of the change: a closed list issues no
 * request and adds no rows to the document.
 */
export function CampaignRecipients({
  campaign,
  filter,
  onFilterChange,
  refreshKey,
  open,
  onOpenChange,
}: CampaignRecipientsProps) {
  const materialised = campaign.progress.materialisedAt !== null;
  /* The same reading the progress bar above is drawn from, so the chip and the
     bar cannot disagree about how many people are on this list. It comes off
     the campaign response, never off the list request — which is what lets the
     count be shown without asking for the rows. */
  const total = campaignProgressReading(campaign.progress).total;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>Recipients</CardTitle>
            <CardDescription>{describeList(campaign)}</CardDescription>
          </div>

          {/* No control on a campaign with no list: there is nothing to open,
              and a button that reveals a paragraph saying so is worse than the
              paragraph. */}
          {materialised && (
            <div className="flex shrink-0 items-center gap-2">
              <Badge tone="neutral">{pluralise(total, 'recipient')}</Badge>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-expanded={open}
                onClick={() => {
                  onOpenChange(!open);
                }}
              >
                <ListFilter aria-hidden="true" className="size-4" />
                {open ? 'Hide recipients' : 'Show recipients'}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      {!materialised ? (
        <CardContent>
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            No recipient list yet. Scheduling builds it — that is when Wend de-duplicates the
            segment, checks every address against your suppression list and the send allow-list, and
            records what will happen to each person.
          </p>
        </CardContent>
      ) : open ? (
        <CardContent className="flex flex-col gap-4">
          <RecipientList
            campaign={campaign}
            filter={filter}
            onFilterChange={onFilterChange}
            refreshKey={refreshKey}
          />
        </CardContent>
      ) : null}
    </Card>
  );
}

function RecipientList({
  campaign,
  filter,
  onFilterChange,
  refreshKey,
}: Omit<CampaignRecipientsProps, 'open' | 'onOpenChange'>) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  /*
   * NARROWING THE LIST GOES BACK TO ITS FIRST PAGE.
   *
   * `filter` is owned by `CampaignDetailPage`, and the four places that set it
   * — three deep links and the quick-filter chips — all set it alone. `page` is
   * owned here, so it survived every one of them: chip "Skipped" on page 5 of
   * 300 recipients asked the API for page 5 of the 40 skipped ones and got an
   * empty page back. The empty state then had to decide what an empty page
   * means, and the answer depends on state it does not own.
   *
   * Resetting here rather than at the four call sites is the point: a fifth
   * link is written by somebody who has never read this file, and a filter that
   * lands on page 5 is not a bug they would think to look for.
   */
  useEffect(() => {
    setPage(1);
  }, [filter.status, filter.skipReason]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listCampaignRecipients(
        campaign.id,
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          ...(filter.status === null ? {} : { status: filter.status }),
          ...(filter.skipReason === null ? {} : { skipReason: filter.skipReason }),
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [campaign.id, filter.status, filter.skipReason, search, page, refreshKey],
  );

  const columns = useMemo<Array<DataTableColumn<CampaignRecipient>>>(
    () => [
      {
        key: 'recipient',
        header: 'Recipient',
        cell: (recipient) => (
          <div className="min-w-0">
            {recipient.name === null ? (
              <p className="text-muted">No name in the list</p>
            ) : (
              <p className="truncate text-text">{recipient.name}</p>
            )}
            {recipient.address === null ? (
              // An email campaign shows nothing here for a contact that has only
              // a phone number — answering "which address?" with a phone number
              // would be answering a question nobody asked.
              <p className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
                no email address
              </p>
            ) : (
              <p className="break-all font-mono text-[0.8rem] text-muted">{recipient.address}</p>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '20rem',
        cell: (recipient) => (
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={RECIPIENT_STATUS_DISPLAY[recipient.status].tone}>
                {RECIPIENT_STATUS_DISPLAY[recipient.status].label}
              </Badge>
              {recipient.skipReason !== null && (
                <Badge tone={SKIP_REASON_TONE[recipient.skipReason]}>
                  {CAMPAIGN_SKIP_REASONS[recipient.skipReason].label}
                </Badge>
              )}
            </div>

            {/*
              THE MESSAGE STATUS STAYS ON THE ROW, AND ONLY THE SKIP REASON GOES
              TO THE LEGEND — because only one of the two is settled by a badge.

              Slice 25 moved the whole "What happened" column into a legend under
              the table. Review found that half of it could not go: the legend is
              keyed on a badge, and the recipient badge does **not** determine
              the message status. Two rows both badged `Failed` can be an address
              Amazon refused and one Amazon accepted whose record was never
              written — opposite claims about whether anything left SES — and a
              legend keyed on `Failed` has to pick one of them. A tenant told
              "Amazon refused it. Nothing was delivered." about the second sends
              it again by hand, which is the one thing the send runner says must
              not happen.

              The same hole swallowed the `nothing yet` state: `pending` with a
              claimed message and `pending` with no message at all wear one
              badge, and only the sentence told them apart.

              A skip reason has no such ambiguity — it *is* the second badge on
              the row — so those sentences are still in the legend, which is
              where the measured defect was: the suppression caveat printed five
              times on a page and twenty-five times filtered to skipped.
            */}
            {recipient.skipReason === null && (
              <span className="text-[0.8rem] leading-snug text-muted">
                {recipient.messageStatus === null
                  ? 'Nothing yet — no message has been created for this person.'
                  : messageStatusMeaning(recipient.messageStatus, campaign.channel)}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'sentAt',
        header: 'Sent',
        width: '11rem',
        cell: (recipient) =>
          recipient.sentAt === null ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="whitespace-nowrap text-muted">{formatDateTime(recipient.sentAt)}</span>
          ),
      },
    ],
    // The channel is now read inside a cell, so it belongs here: a campaign
    // cannot change channel, but a memo that closes over a value it does not
    // depend on is a stale render waiting for the day one can.
    [campaign.channel],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <>
      {failedToLoad ? (
        <ErrorState
          error={error}
          title="Could not load the recipients"
          onRetry={reload}
          className="flex flex-col items-start gap-3 py-2"
        />
      ) : (
        <>
            <div className="flex flex-wrap items-center gap-2">
              <QuickFilters campaign={campaign} filter={filter} onFilterChange={onFilterChange} />

              <div className="relative ml-auto min-w-0 flex-1 sm:max-w-xs">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder="Search by address or name"
                  aria-label="Search recipients"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                  }}
                />
              </div>

              {refreshing && <Spinner size="sm" label="Updating the recipients" />}
            </div>

            {filter.skipReason !== null && (
              <Callout
                // Always `info`, whatever the reason's chip tone: this is an
                // explanation of a decision Wend took deliberately, not a fault
                // being reported.
                tone="info"
                title={CAMPAIGN_SKIP_REASONS[filter.skipReason].label}
                action={
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      onFilterChange(NO_RECIPIENT_FILTER);
                      setPage(1);
                    }}
                  >
                    <X aria-hidden="true" className="size-4" />
                    Show everyone again
                  </Button>
                }
              >
                {CAMPAIGN_SKIP_REASONS[filter.skipReason].sentence}
              </Callout>
            )}

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The rows below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Campaign recipients"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(recipient) => recipient.id}
              loading={loadingFirst}
              minWidth="38rem"
              /*
               * FOUR WAYS TO GET AN EMPTY TABLE, AND ONLY TWO OF THEM ARE A
               * FILTER. AN EMPTY *PAGE* IS THE FOURTH AND IT IS NOT ANY OF THEM.
               *
               * The list was previously either "no recipient matches that
               * search" or "nobody matches that filter" — and the second was
               * printed with no filter applied, on the one case that is not a
               * filter at all: a campaign whose recipient list was built and
               * came out empty, because the segment reached nobody on this
               * channel. That is a fact about the campaign, it is the reason it
               * will send nothing, and it was being reported as a search result.
               *
               * **Every absolute claim here is now gated on `total === 0`, and
               * the first version gated only one of them.** It reasoned about
               * exactly this — that the chips did not reset `page`, so a
               * narrowing that leaves fewer pages than the one being viewed
               * renders nothing over a list with plenty — and then put the guard
               * on the no-filter branch alone, which the ternary reaches last.
               * `filter.status !== null` short-circuited first, so page 5 of a
               * 40-row "Skipped" list read "Nobody on this campaign matches that
               * filter" over the forty people the send guard skipped. The reader
               * concludes the guard skipped nobody, on the screen whose whole
               * job is showing them who it skipped.
               *
               * The page reset above makes that state rare. It does not make it
               * impossible — the list is live, `refreshKey` moves while a send
               * runs, and rows can leave the page under a reader — so the
               * sentence has to be true whether or not the reset fired, which is
               * why the guard is here as well and not instead.
               *
               * `total` is the filtered total: the line under this table says
               * "N recipients match this filter" from the same number. So
               * `total > 0` with no rows means precisely "this page is past the
               * end", which is a navigation fact and gets a way back rather than
               * a claim about anybody.
               */
              empty={
                pagination !== null && pagination.total > 0 ? (
                  <div className="flex flex-col items-start gap-3">
                    <p className="max-w-prose text-[0.9rem] text-muted">
                      Nothing on page {pagination.page}. This list has{' '}
                      {pluralise(pagination.total, 'row')} — the page is past the end of it, not
                      empty of matches.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setPage(1);
                      }}
                    >
                      Back to the first page
                    </Button>
                  </div>
                ) : (
                  <p className="max-w-prose text-[0.9rem] text-muted">
                    {search !== ''
                      ? `No recipient of this campaign matches “${search}”.`
                      : filter.status !== null || filter.skipReason !== null
                        ? 'Nobody on this campaign matches that filter.'
                        : pagination !== null
                          ? 'This campaign has an empty recipient list: nobody in the segment could be reached on this channel, so there is nobody to send to.'
                          : 'Nothing on this page of the list.'}
                  </p>
                )
              }
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />

          {pagination !== null && pagination.total > 0 && (
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              {pluralise(pagination.total, 'recipient')} match this filter
            </p>
          )}

          <RecipientBadgeLegend rows={data?.items ?? []} />
        </>
      )}
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Filters                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The four questions worth one click.
 *
 * The waiting filter is labelled by what waiting *means* on this campaign: on a
 * scheduled one it is the list of people who will actually receive the message,
 * which is the single most useful thing this screen can show before a send. Once
 * the send has run, the same rows are simply what is left.
 *
 * Slice 66 draws them with the shared `ScopeChips`, single-select: the same
 * `role="group"` of `aria-pressed` buttons it always was, in the Blueprint's
 * chip skin instead of a hand-rolled one. Pressing a chip still sets the status
 * and clears any skip reason — including pressing the chip that is already lit,
 * which is how "Skipped" clears a reason filter. A status no chip offers (a
 * deep link to failed recipients) lights no chip, as before: the
 * clear-filter control above the table is how a reader leaves it.
 */
function QuickFilters({
  campaign,
  filter,
  onFilterChange,
}: {
  campaign: Campaign;
  filter: RecipientFilter;
  onFilterChange: (filter: RecipientFilter) => void;
}) {
  const waitingLabel = campaign.status === 'scheduled' ? 'Will receive it' : 'Waiting';

  const choices: ReadonlyArray<{ key: string; label: string; status: RecipientStatus | null }> = [
    { key: 'all', label: 'Everyone', status: null },
    { key: 'pending', label: waitingLabel, status: 'pending' },
    { key: 'sent', label: 'Sent', status: 'sent' },
    { key: 'skipped', label: 'Skipped', status: 'skipped' },
  ];

  // A reason filter implies `skipped`, so the Skipped chip stays lit while one
  // is active rather than leaving no chip selected at all.
  const pressed =
    filter.status === null && filter.skipReason === null
      ? 'all'
      : filter.skipReason !== null
        ? 'skipped'
        : (choices.find((choice) => choice.status === filter.status)?.key ?? '');

  return (
    <ScopeChips
      label="Filter recipients"
      selection="single"
      value={pressed}
      items={choices.map((choice) => ({ value: choice.key, label: choice.label }))}
      onValueChange={(key) => {
        const choice = choices.find((entry) => entry.key === key);
        if (choice !== undefined) onFilterChange({ status: choice.status, skipReason: null });
      }}
    />
  );
}

/** What this list is, in one line, given where the campaign has got to. */
function describeList(campaign: Campaign): string {
  switch (campaign.status) {
    case 'draft':
      return 'A draft has no recipients. Scheduling the campaign is what builds the list.';
    case 'scheduled':
      return 'The waiting rows are the addresses that will actually receive this. Everything else has already been decided, and says why.';
    case 'sending':
      return 'Updating as the send runs. The waiting rows are still to go; the skipped ones were decided when the list was built.';
    default:
      return 'What happened to every person this campaign touched, including everybody it deliberately did not write to, and the reason for each.';
  }
}
